import { Currency } from "@lookiero/sty-psp-locale";
import { Order, ServiceStatus } from "../../../domain/entities/order/Order";
import { Product } from "../../../domain/entities/product/Product";
import { OrderSummaryForCheckout } from "../../../domain/projections/OrderSummaryForCheckout";
import { Price } from "../../../domain/projections/Price";
import { ReturningOrderPaymentBreakdown } from "../../../domain/projections/ReturningOrderPaymentBreakdown";
import { DEFAULT_CURRENCY } from "../../ui/shared/config";

interface PromoDto {
  readonly code: string | null;
  readonly discountPercentage: number | null;
}

interface OrderProductDTO {
  readonly brand: string;
  readonly id?: string;
  readonly itemId?: string;
  readonly itemStatus?: string; // "SELECTED", "DISCARDED", "UNAVAILABLE", "RETURN_DONE", "TO_RETURN"
  readonly imageUrl: string;
  readonly price: {
    readonly amount: number;
    readonly currency: string;
    readonly discountedPrice: {
      readonly amount: number;
      readonly percentage: number;
    };
  };
  readonly reference: string;
  readonly title: string;
  readonly variantId: string;
  readonly wishlisted: boolean;
  readonly itemPrice?: Price;
  readonly size: string;
  readonly available: boolean;
}

interface OrderDTO {
  readonly bookingId?: string;
  readonly currency?: string;
  readonly datePlanned?: string;
  readonly initialPrice?: number;
  readonly items?: OrderProductDTO[];
  readonly orderId: string;
  readonly orderReference?: string; // TODO: once backend is ready leave only reference and remove this one
  readonly orderPrice: {
    readonly amount: number;
    readonly currency: Currency;
  };
  readonly orderOriginalPrice: {
    readonly amount: number;
    readonly currency: Currency;
  };
  readonly price?: number;
  readonly discountedPriceByPromo: Price;
  readonly discountedPriceBySales: Price;
  readonly reference?: string;
  readonly serviceId: string;
  readonly serviceStatus?: string;
  readonly promo: PromoDto;
}

interface OrderDetailsItemDTO {
  readonly itemId: string;
  readonly variantId: string;
  readonly imageUrl: string;
  readonly itemStatus: string;
  readonly price: number;
  readonly itemPrice: Price & {
    readonly discountedPrice: {
      readonly amount: number;
      readonly percentage: number;
    };
  };
  readonly color: string;
  readonly size: string;
  readonly brand: string;
  readonly title: string;
  readonly feedback: {
    readonly comments: string[];
    readonly answers: string[];
  };
}

interface OrderDetailsDTO {
  readonly details: {
    readonly addressId: string;
    readonly currency: string;
    readonly orderId: string;
    readonly serviceId: string;
    readonly orderStatus: string;
    readonly serviceStatus: string;
    readonly reference: string;
    readonly datePlanned: string;
    readonly initialPrice: number;
    readonly discardedItemsPrice: number;
    readonly returnedItemsPrice: number;
    readonly pendingReturnItemsPrice: number;
    readonly discountAmount: number;
    readonly totalPrice: number;
    readonly orderOriginalPrice: Price;
    readonly discountedPriceByPromo: Price;
    readonly discountedPriceBySales: Price;
    readonly promo: {
      readonly code: string | null;
      readonly discountPercentage: number | null;
    };
    readonly items: OrderDetailsItemDTO[];
  };
}

interface BreakdownDTO {
  readonly total: number;
  readonly breakdown: {
    readonly balance: number;
    readonly paymentInstrument: number;
  };
}

// TODO: remove this once we model the product correctly
interface ProductDTO {
  readonly brand: string;
  readonly id?: string;
  readonly itemId?: string;
  readonly itemStatus?: string; // "SELECTED", "DISCARDED", "UNAVAILABLE", "RETURN_DONE", "TO_RETURN"
  readonly imageUrl: string;
  readonly price: {
    readonly amount: number;
    readonly currency: string;
  };
  readonly reference: string;
  readonly title: string;
  readonly variantId: string;
  readonly wishlisted: boolean;
  readonly itemPrice?: Price;
}

interface ReturningOrderPaymentBreakdownDTO {
  readonly total: number;
  readonly breakdown: {
    readonly paymentInstrument: number;
    readonly balance: number;
  };
}

const mapProductDTOtoProduct = (productDTO: ProductDTO): Product => {
  return new Product({
    ...productDTO,
    itemId: productDTO.id ? productDTO.id : productDTO.itemId,
    price: productDTO.itemPrice || productDTO.price,
    // oxlint-disable-next-line typescript/no-explicit-any
  } as any);
};

const mapOrderDTOtoOrder = (orderDTO: OrderDTO): Order => {
  const currency = orderDTO.currency ? orderDTO.currency : DEFAULT_CURRENCY;
  return new Order({
    ...orderDTO,
    items: orderDTO.items ? orderDTO.items.map((orderItem) => mapProductDTOtoProduct(orderItem)) : [],
    currency,
    price: orderDTO.initialPrice ? orderDTO.initialPrice : orderDTO.price,
    reference: orderDTO.orderReference ? orderDTO.orderReference : orderDTO.reference,
    totalPrice: orderDTO.orderPrice ? orderDTO.orderPrice.amount : orderDTO.initialPrice,
    orginalTotalPrice: orderDTO.orderOriginalPrice ? orderDTO.orderOriginalPrice.amount : orderDTO.initialPrice,
    discountAmount: orderDTO.discountedPriceByPromo ? orderDTO.discountedPriceByPromo.amount : 0,
    discountedPriceBySales: orderDTO.discountedPriceBySales ? orderDTO.discountedPriceBySales.amount : 0,
    promo:
      orderDTO.promo && orderDTO.promo.code && orderDTO.promo.discountPercentage
        ? {
            code: orderDTO.promo.code,
            discountPercentage: orderDTO.promo.discountPercentage,
          }
        : undefined,
    // oxlint-disable-next-line typescript/no-explicit-any
  } as any);
};

const mapOrdersDTOtoOrders = (ordersDTO: OrderDTO[]): Order[] => {
  return ordersDTO.map((order: OrderDTO) => {
    return new Order({
      ...order,
      items: order.items ? order.items.map((orderItem) => mapProductDTOtoProduct(orderItem)) : [],
      totalPrice: order.initialPrice,
      datePlanned: order.datePlanned || "",
      orginalTotalPrice: order.initialPrice,
      currency: order.currency,
      promo: undefined,
      // oxlint-disable-next-line typescript/no-explicit-any
    } as any);
  });
};

const mapOrdersDTOtoCompletedOrders = (ordersDTO: OrderDTO[]): Order[] => {
  return ordersDTO.reduce<Order[]>((acc, currentOrder) => {
    if (currentOrder.serviceStatus === ServiceStatus.CREATED) {
      return acc;
    }

    const items = currentOrder.items
      ? currentOrder.items
          .map(
            (orderItem) =>
              new Product({
                ...orderItem,
                itemId: orderItem.id ? orderItem.id : orderItem.itemId,
                price: orderItem.itemPrice || orderItem.price,
                // oxlint-disable-next-line typescript/no-explicit-any
              } as any),
          )
          .sort((a, b) => (Number(a.itemId) > Number(b.itemId) ? 1 : -1))
      : [];

    return [
      ...acc,
      new Order({
        ...currentOrder,
        items,
        totalPrice: currentOrder.initialPrice,
        datePlanned: currentOrder.datePlanned || "",
        orginalTotalPrice: currentOrder.initialPrice,
        currency: currentOrder.currency,
        promo: undefined,
        // oxlint-disable-next-line typescript/no-explicit-any
      } as any),
    ];
  }, []);
};

const mapOrderDetailDTOtoOrder = (orderDetailsDTO: OrderDetailsDTO): Order => {
  const orderDetails = orderDetailsDTO.details;
  return new Order({
    ...orderDetails,
    items: orderDetails.items.map(
      (orderItem) =>
        new Product({
          ...orderItem,
          price: orderItem.itemPrice,
          // oxlint-disable-next-line typescript/no-explicit-any
        } as any),
    ),
    currency: orderDetails.currency ? orderDetails.currency : DEFAULT_CURRENCY,
    discountAmount: orderDetails.discountedPriceByPromo ? orderDetails.discountedPriceByPromo.amount : 0,
    promo:
      orderDetails.promo && orderDetails.promo.code && orderDetails.promo.discountPercentage
        ? {
            code: orderDetails.promo.code,
            discountPercentage: orderDetails.promo.discountPercentage,
          }
        : undefined,
    orginalTotalPrice: orderDetails.orderOriginalPrice
      ? orderDetails.orderOriginalPrice.amount
      : orderDetails.totalPrice,
    // oxlint-disable-next-line typescript/no-explicit-any
  } as any);
};

const mapReturningOrderPaymentBreakdownDTO = (
  breakdown: ReturningOrderPaymentBreakdownDTO,
  currency: Currency,
): ReturningOrderPaymentBreakdown => {
  const amounts = breakdown.breakdown;

  return {
    payment: {
      amount: amounts.paymentInstrument,
      currency,
    },
    balance: {
      amount: amounts.balance,
      currency,
    },
  };
};

const mapOrderDtoAndBreakDtoToOrderSummaryForCheckout = (
  orderDto: OrderDTO,
  breakdownDto: BreakdownDTO,
): OrderSummaryForCheckout => {
  return {
    id: orderDto.orderId,
    items: orderDto.items ? orderDto.items.map((orderItem) => mapProductDTOtoProduct(orderItem)) : [],
    grossPrice: {
      amount: orderDto.orderOriginalPrice.amount,
      currency: orderDto.orderOriginalPrice.currency,
    },
    netPrice: {
      amount: breakdownDto.breakdown.paymentInstrument,
      currency: orderDto.orderOriginalPrice.currency,
    },
    salesDiscount: {
      amount: orderDto.discountedPriceBySales.amount,
      currency: orderDto.discountedPriceBySales.currency,
    },
    balance: {
      amount: breakdownDto.breakdown.balance,
      currency: orderDto.orderOriginalPrice.currency,
    },
    promo: {
      code: orderDto.promo.code,
      percentage: orderDto.promo.discountPercentage,
      discount: {
        amount: orderDto.discountedPriceByPromo.amount,
        currency: orderDto.discountedPriceByPromo.currency,
      },
    },
  };
};

export type { OrderDTO, BreakdownDTO, OrderDetailsDTO };
export {
  mapProductDTOtoProduct,
  mapOrderDTOtoOrder,
  mapOrdersDTOtoOrders,
  mapOrdersDTOtoCompletedOrders,
  mapOrderDetailDTOtoOrder,
  mapReturningOrderPaymentBreakdownDTO,
  mapOrderDtoAndBreakDtoToOrderSummaryForCheckout,
};
